import React, { useState, useCallback, useEffect } from 'react';
import {
  AppointmentDateRangeType,
  useGetEstimatedSendTimeForMassTextAlertLazyQuery,
  useGetActivityLogForMassTextAlertsQuery,
  SortOrder,
} from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import MassTextAlertsActivitlyLogItem from './components/MassTextAlertsActivityLogItem';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import MassTextAlertsCustomDateRangeModal from './components/MassTextAlertsCustomDateRangeModal';
import MassTextAlertsSendConfirmationModal from './components/MassTextAlertsSendConfirmationModal';
import format from 'date-fns-tz/format';
import MassTextAlertsMessageCompose from './components/MassTextAlertsMessageComposer';
import MassTextAlertsDateRangeSelector from './components/MassTextAlertsDateRangeSelector';
import { Button, useDisclosure, Box, Heading, Text, Modal, ModalHeader, ModalCloseButton } from '@televet/kibble-ui';
import { PersistedStateKeys } from 'shared/enums/PersistedStateKeys';
import { useTracking } from 'shared/providers/TrackingProvider';
import { MassTextAlertsModalName } from './records/MassTextAlertsModalName';
import { MassTextAlertEvents } from 'shared/providers/TrackingProvider/EventNames';

export type SelectedDateRange = {
  optionName?: AppointmentDateRangeType | undefined;
  startDate?: Date | undefined;
  endDate?: Date | undefined;
};

export type DateStringObject = {
  date: string;
  isoString: string;
  shortDate: string;
  time: string;
};

const MassTextAlertsPanel = (): JSX.Element => {
  // Providers
  const { currentClinicId, currentClinic } = useClinicUser();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { trackEvent } = useTracking();

  // State
  const [selectedDateRange, setSelectedDateRange] = useState<SelectedDateRange | null>(null);
  const [remainingCharCount, setRemainingCharCount] = useState<number>(450);
  const [messageText, setMessageText] = useState<string>('');
  const [selectedModal, setSelectedModal] = useState<MassTextAlertsModalName | null>(null);
  const [allAlertsLoaded, setAllAlertsLoaded] = useState(false);

  // Queries
  const [getEstimatedSendTime, { data: sendTimeData, loading: sendTimeDataLoading }] =
    useGetEstimatedSendTimeForMassTextAlertLazyQuery({
      fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
    });

  const {
    data: activityLogData,
    refetch: refetchActivityLogData,
    fetchMore: fetchMoreActivityLogData,
  } = useGetActivityLogForMassTextAlertsQuery({
    variables: {
      where: {
        clinicId: {
          equals: currentClinicId,
        },
      },
      skip: 0,
      take: 10,
      orderBy: {
        createdAt: SortOrder.Desc,
      },
    },
  });

  const loadMoreAlerts = useCallback(async () => {
    const skip = activityLogData?.findManyMassTextAlert?.length || 0;
    await fetchMoreActivityLogData({
      variables: {
        skip,
      },
    });
  }, [activityLogData?.findManyMassTextAlert?.length, fetchMoreActivityLogData]);

  useEffect(() => {
    if (!activityLogData?.findManyMassTextAlert) return;

    if (activityLogData.findManyMassTextAlert.length === activityLogData.findManyMassTextAlertCount) {
      setAllAlertsLoaded(true);
    }
  }, [activityLogData]);

  // Set data from local storage on load if available
  useEffect(() => {
    const draftMessage = localStorage.getItem(PersistedStateKeys.MassTextAlertsDraftMessage);

    if (draftMessage) {
      setMessageText(draftMessage);
      setRemainingCharCount(450 - draftMessage.length);
    }
    // eslint-disable-next-line
  }, []);

  const formatDateString = useCallback(
    (dateString: Date | undefined): DateStringObject | undefined => {
      if (!dateString) return;

      const clinicTimezone = currentClinic?.timezoneName || 'UTC';

      const date = new Date(dateString).toLocaleDateString([], { day: 'numeric', year: '2-digit', month: 'numeric' });
      const shortDate = new Date(dateString).toLocaleDateString([], {
        weekday: 'short',
        day: 'numeric',
        month: 'numeric',
      });
      const time = new Date(dateString).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      const isoString = format(new Date(dateString), `yyyy-MM-dd'T'HH:mm:ssXXX`, { timeZone: clinicTimezone });

      return {
        date,
        isoString,
        shortDate: shortDate.replace(',', ''),
        time: time.replace(' ', '').toLocaleLowerCase(),
      };
    },
    [currentClinic],
  );

  const formatTimeEstimate = useCallback(() => {
    if (sendTimeDataLoading) return;

    const totalSeconds = sendTimeData?.estimateNumberOfPetParentsReached?.timeEstimateInSeconds;
    let result = '';

    if (totalSeconds) {
      const minutes = Math.floor(totalSeconds / 60);

      result = `Less than ${minutes + 1} minute`;
    }

    return result;
  }, [sendTimeData, sendTimeDataLoading]);

  const formatDateRange = useCallback(
    (dateRange: { startDate?: Date; endDate?: Date }): string => {
      let formattedDateRange = '';

      if (dateRange.startDate && dateRange.endDate) {
        const start = formatDateString(dateRange.startDate);
        const end = formatDateString(dateRange.endDate);

        if (start && end) {
          formattedDateRange = `between ${start.shortDate} at ${start.time} - ${end.shortDate} at ${end.time}`;

          if (start.date === end.date) {
            if (start.time === '12:00am') {
              formattedDateRange = `on ${start.date}`;
            } else {
              formattedDateRange = `on ${start.shortDate} between ${start.time} - ${end.time}`;
            }
          } else {
            if (start.time === '12:00am') {
              formattedDateRange = `between ${start.shortDate} - ${end.shortDate}`;
            }
          }
        }
      }

      return formattedDateRange;
    },
    [formatDateString],
  );

  const fetchEstimatedSendTime = useCallback(
    (startDate?: Date, endDate?: Date) => {
      if (!currentClinicId || !startDate || !endDate) return;

      const isoFomattedStartDate = formatDateString(new Date(startDate));
      const isoFormattedEndDate = formatDateString(new Date(endDate));

      if (isoFomattedStartDate && isoFormattedEndDate) {
        getEstimatedSendTime({
          variables: {
            clinicId: currentClinicId,
            appointmentDateTimeTo: isoFormattedEndDate.isoString,
            appointmentDateTimeFrom: isoFomattedStartDate.isoString,
            alertBody: messageText,
          },
        });
      }
    },
    [currentClinicId, getEstimatedSendTime, messageText, formatDateString],
  );

  const handleSetCustomDateRange = useCallback(
    (selectionRange) => {
      const data = {
        optionName: AppointmentDateRangeType.Custom,
        startDate: selectionRange.startDate,
        endDate: selectionRange.endDate,
      };

      setSelectedDateRange(data);
      fetchEstimatedSendTime(selectionRange.startDate, selectionRange.endDate);
      trackEvent(MassTextAlertEvents.SetCustomDateRange, { ...data });
    },
    [fetchEstimatedSendTime, trackEvent],
  );

  const handleOpenModal = (modalName: MassTextAlertsModalName): void => {
    setSelectedModal(modalName);

    setTimeout(() => {
      onOpen();
    }, 300);
  };

  const handleCloseModal = (): void => {
    setSelectedModal(null);
    onClose();
  };

  return (
    <>
      <Box className="MassAlert__Container" h="100%" overflowY="scroll">
        <Box py={4} px={5}>
          <Heading className="MassAlert__Header" size="lg">
            Mass Alerts
          </Heading>
        </Box>
        <Box className="MassAlert__SubHeader" borderY="1px" borderColor="border.default" py={4} px={5}>
          <Text size="lg" fontWeight="bold">
            Compose Mass Text Alert
          </Text>
        </Box>
        <Box className="MassAlert__ComposerContainer" p={5} bg="background.subtle">
          <MassTextAlertsDateRangeSelector
            fetchEstimatedSendTime={fetchEstimatedSendTime}
            setSelectedDateRange={setSelectedDateRange}
            selectedDateRange={selectedDateRange}
            formatDateRange={formatDateRange}
            sendTimeData={sendTimeData?.estimateNumberOfPetParentsReached}
            openModal={handleOpenModal}
          />
          <MassTextAlertsMessageCompose
            messageText={messageText}
            setMessageText={setMessageText}
            remainingCharCount={remainingCharCount}
            selectedDateRange={selectedDateRange}
            setRemainingCharCount={setRemainingCharCount}
            sendTimeData={sendTimeData?.estimateNumberOfPetParentsReached}
            fetchEstimatedSendTime={fetchEstimatedSendTime}
            formatTimeEstimate={formatTimeEstimate}
            openModal={handleOpenModal}
          />
        </Box>
        <Box className="MassAlert__SubHeader" borderY="1px" borderColor="border.default" py={4} px={5}>
          <Text size="lg" fontWeight="bold">
            Mass Text Alert Activity
          </Text>
        </Box>
        <Box className="MassAlert__ActivityLogContainer">
          {!activityLogData ||
            (activityLogData?.findManyMassTextAlert?.length === 0 && (
              <Text
                className="MassAlert__EmptyState"
                textAlign="center"
                as="p"
                mt={5}
                variant="subtle"
                fontStyle="italic"
                size="sm"
              >
                No data available at this time
              </Text>
            ))}
          {activityLogData &&
            activityLogData?.findManyMassTextAlert?.map((log) => {
              return (
                <MassTextAlertsActivitlyLogItem
                  key={log.id}
                  clinicId={currentClinicId || ''}
                  logItem={log}
                  sendTimeData={sendTimeData?.estimateNumberOfPetParentsReached}
                  formattedDateRange={formatDateRange({
                    startDate: log.appointmentDateTimeFrom,
                    endDate: log.appointmentDateTimeTo,
                  })}
                  refetchActivityLogData={refetchActivityLogData}
                  formattedDateString={formatDateString(log.createdAt)}
                />
              );
            })}
          {!allAlertsLoaded && (
            <Button
              variant="ghostNeutral"
              display="block"
              m="auto"
              mt={5}
              onClick={loadMoreAlerts}
              _hover={{ textDecor: 'underline' }}
              _focus={{ textDecor: 'none' }}
              _active={{ textDecor: 'underline' }}
            >
              Load More
            </Button>
          )}
        </Box>
      </Box>
      <Modal
        isOpen={isOpen}
        onClose={handleCloseModal}
        size={selectedModal === MassTextAlertsModalName.SelectMassTextAlertCustomDateRange ? 'md' : 'sm'}
        isCentered
      >
        <ModalHeader>
          {selectedModal === MassTextAlertsModalName.SelectMassTextAlertCustomDateRange
            ? 'Select Custom Date Range'
            : 'Confirm Send?'}
        </ModalHeader>
        <ModalCloseButton />
        {selectedModal === MassTextAlertsModalName.SelectMassTextAlertCustomDateRange && (
          <MassTextAlertsCustomDateRangeModal
            setSelectionRange={handleSetCustomDateRange}
            selectionRange={selectedDateRange}
            onCloseModal={handleCloseModal}
          />
        )}
        {selectedModal === MassTextAlertsModalName.SendMassTextAlertConfirmation && (
          <MassTextAlertsSendConfirmationModal
            estimatedSendTimeData={sendTimeData?.estimateNumberOfPetParentsReached}
            setSelectedDateRange={setSelectedDateRange}
            selectedDateRange={selectedDateRange}
            refetchActivityLogData={refetchActivityLogData}
            messageText={messageText}
            setMessageText={setMessageText}
            setRemainingCharCount={setRemainingCharCount}
            formatTimeEstimate={formatTimeEstimate}
            onCloseModal={handleCloseModal}
            formatDateString={formatDateString}
          />
        )}
      </Modal>
    </>
  );
};

export default MassTextAlertsPanel;
